import React, { useEffect, useMemo } from 'react';

const COLORS = ['#247BA0', '#37B49A', '#A5FFD6', '#FFD166', '#EF476F', '#8338EC'];

// Lightweight, dependency-free confetti burst: a fixed overlay of colored
// pieces falling with randomized position/rotation/timing, built with plain
// CSS keyframes (see the injected <style> below) rather than a canvas
// library, since nothing like that is in this app's dependency tree.
function ConfettiPieces({ count = 90 }) {
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    color: COLORS[i % COLORS.length],
    size: 6 + Math.random() * 6,
    duration: 2.6 + Math.random() * 1.8,
    delay: Math.random() * 0.6,
    rotation: Math.random() * 360,
    drift: (Math.random() - 0.5) * 160,
    round: Math.random() > 0.5,
  })), [count]);

  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-[10001]">
      {pieces.map(p => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            top: '-5%',
            left: `${p.left}%`,
            width: p.size,
            height: p.size * (p.round ? 1 : 1.6),
            backgroundColor: p.color,
            borderRadius: p.round ? '50%' : '2px',
            transform: `rotate(${p.rotation}deg)`,
            animation: `confetti-fall ${p.duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${p.delay}s forwards`,
            '--drift': `${p.drift}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translate3d(0, 0, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate3d(var(--drift), 105vh, 0) rotate(540deg); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}

// Full celebration: confetti burst + a centered "you finished a chapter"
// card. Auto-dismisses, but can also be tapped away.
export default function ChapterCelebration({ open, bookName, chapter, onClose, autoDismissMs = 4500 }) {
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => onClose?.(), autoDismissMs);
    return () => clearTimeout(timer);
  }, [open, autoDismissMs, onClose]);

  if (!open) return null;

  return (
    <>
      <ConfettiPieces />
      <div
        className="fixed inset-0 z-[10002] flex items-center justify-center px-4"
        onClick={onClose}
      >
        <div
          className="bg-white dark:bg-[#111827] rounded-2xl shadow-2xl px-8 py-8 text-center max-w-xs w-full animate-fade-in"
          onClick={e => e.stopPropagation()}
        >
          <div className="text-5xl mb-3">🎉</div>
          <h3 className="text-xl font-bold text-space-cadet dark:text-white mb-1.5">
            Congrats, you finished a chapter!
          </h3>
          {bookName && chapter && (
            <p className="text-sm text-gray-500 dark:text-white/50 mb-5">
              Every verse of {bookName} {chapter} is now recorded.
            </p>
          )}
          <button
            type="button"
            onClick={onClose}
            className="btn-primary w-full"
          >
            Nice!
          </button>
        </div>
      </div>
    </>
  );
}
